import React, { useState, useEffect, useCallback, createContext, useContext } from 'react';
import { Clock, Users, CheckCircle, AlertCircle, MessageSquare, BarChart3, FileText, Plus, Send, ThumbsUp, ThumbsDown, User, Settings, LogOut, ChevronRight, ChevronDown, ChevronUp, X, Mic, MicOff, Mail, Search, RefreshCw, Trash2, XCircle, ArrowLeft, Loader2, Bell, BellOff, Eye, Download, Filter, Columns, ArrowUpDown, Maximize2, Minimize2, ChevronLeft, ShieldAlert, Heart, Phone } from 'lucide-react';
import { useToast, useCrisisAlert, ToastProvider, CrisisContext } from '../shared.jsx';
import { storage, addAuditEntry, calculateWorkingHours, OWL_LOGO, EMAIL_CONFIG, detectHarmContent, notifyMentorOfHarmDetection, sendNotification, VoiceInputButton, useVoiceInput, initFCM, removeFCMToken, notificationPermissionGranted, requestNotificationPermission, CrisisResourceModal, getUrlParams, clearUrlParams, initFirebase } from '../shared.jsx';

function ConflictHistory({ conflict }) {
  const [activeSection, setActiveSection] = useState('activity');
  
  // Merge status history and audit log into a single timeline, sorted newest first
  const statusEntries = (conflict.statusHistory || []).map(e => ({
    type: 'status',
    timestamp: e.timestamp,
    status: e.status
  }));
  const auditEntries = (conflict.auditLog || []).map(e => ({
    type: 'audit',
    timestamp: e.timestamp,
    action: e.action,
    details: e.details,
    userName: e.userName,
    userEmail: e.userEmail
  }));
  const allEntries = [...statusEntries, ...auditEntries].sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));

  const statusColors = {
    'pending-acceptance': 'bg-yellow-500',
    'changes-proposed': 'bg-orange-500',
    'created': 'bg-blue-500',
    'identify-define': 'bg-purple-500',
    'communicate': 'bg-orange-500',
    'explore-alternatives': 'bg-amber-500',
    'evaluate-select': 'bg-yellow-500',
    'agree-implement': 'bg-teal-500',
    'follow-up': 'bg-cyan-500',
    'resolved': 'bg-green-500'
  };

  return (
    <div className="space-y-4">
      {/* Section Tabs */}
      <div className="flex gap-2 mb-4">
        {['activity', 'phases', 'comments'].map(section => (
          <button
            key={section}
            onClick={() => setActiveSection(section)}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors capitalize ${
              activeSection === section
                ? 'bg-amber-700 text-white'
                : 'bg-stone-100 text-stone-600 hover:bg-stone-200'
            }`}
          >
            {section === 'activity' ? `Activity (${allEntries.length})` : section === 'phases' ? 'Phases' : `Comments (${conflict.comments?.length || 0})`}
          </button>
        ))}
      </div>

      {/* Activity Timeline — merged audit + status */}
      {activeSection === 'activity' && (
        <div className="relative">
          {/* Vertical line */}
          <div className="absolute left-[11px] top-4 bottom-4 w-0.5 bg-stone-200" />
          
          <div className="space-y-0">
            {allEntries.map((entry, idx) => (
              <div key={idx} className="relative flex gap-4 py-3">
                {/* Dot */}
                <div className={`relative z-10 w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 ${
                  entry.type === 'status'
                    ? (statusColors[entry.status] || 'bg-stone-400')
                    : entry.action?.includes('access snapshot')
                      ? 'bg-amber-100 border-2 border-amber-400'
                      : 'bg-white border-2 border-stone-300'
                }`}>
                  {entry.type === 'status' ? (
                    <div className="w-2 h-2 rounded-full bg-white" />
                  ) : entry.action?.includes('access snapshot') ? (
                    <div className="w-2 h-2 rounded-full bg-amber-500" />
                  ) : (
                    <div className="w-2 h-2 rounded-full bg-stone-400" />
                  )}
                </div>
                
                {/* Content */}
                <div className="flex-1 min-w-0 -mt-0.5">
                  {entry.type === 'status' ? (
                    <>
                      <div className="text-sm font-semibold text-stone-800 capitalize">
                        Phase: {entry.status.replace(/-/g, ' ')}
                      </div>
                      <div className="text-xs text-stone-500">
                        {new Date(entry.timestamp).toLocaleString()}
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="text-sm text-stone-800">
                        <span className="font-medium">{entry.userName}</span>
                        {entry.userEmail && (
                          <span className="text-xs text-stone-500 ml-1">({entry.userEmail})</span>
                        )}
                        <span className="text-stone-500">: </span>
                        <span>{entry.action}</span>
                      </div>
                      {entry.details && (
                        entry.action?.includes('access snapshot') ? (
                          <details className="mt-1">
                            <summary className="text-xs text-amber-700 cursor-pointer hover:text-amber-800">View access details</summary>
                            <div className="text-xs text-stone-500 mt-1 pl-2 border-l-2 border-amber-200 whitespace-pre-wrap">{entry.details}</div>
                          </details>
                        ) : (
                          <div className="text-xs text-stone-500 mt-0.5">{entry.details}</div>
                        )
                      )}
                      <div className="text-xs text-stone-500 mt-0.5">
                        {new Date(entry.timestamp).toLocaleString()}
                      </div>
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>

          {allEntries.length === 0 && (
            <p className="text-stone-500 text-sm text-center py-8">No activity recorded yet.</p>
          )}
        </div>
      )}

      {/* Phase History */}
      {activeSection === 'phases' && (
        <div className="space-y-3">
          {conflict.statusHistory?.map((entry, idx) => (
            <div key={idx} className="flex items-center gap-4 p-4 bg-stone-50 rounded-lg">
              <div className={`w-3 h-3 rounded-full flex-shrink-0 ${statusColors[entry.status] || 'bg-stone-400'}`} />
              <div className="flex-1">
                <div className="font-medium text-stone-800">
                  {entry.status.replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}
                </div>
                <div className="text-sm text-stone-500">
                  {new Date(entry.timestamp).toLocaleString()}
                </div>
              </div>
              {idx > 0 && (
                <div className="text-xs text-stone-500">
                  {(() => {
                    const prev = new Date(conflict.statusHistory[idx - 1].timestamp);
                    const curr = new Date(entry.timestamp);
                    const diffMs = curr - prev;
                    const diffMin = Math.round(diffMs / 60000);
                    if (diffMin < 60) return `${diffMin}m`;
                    const diffHr = Math.round(diffMin / 60);
                    if (diffHr < 24) return `${diffHr}h`;
                    return `${Math.round(diffHr / 24)}d`;
                  })()}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Comments */}
      {activeSection === 'comments' && (
        <>
          {conflict.comments && conflict.comments.length > 0 ? (
            <div className="space-y-3">
              {conflict.comments.map((comment, idx) => (
                <div key={idx} className="p-4 bg-white rounded-lg border border-stone-200">
                  <div className="font-medium text-stone-800 mb-1">{comment.author}</div>
                  <p className="text-stone-600">{comment.text}</p>
                  <div className="text-xs text-stone-500 mt-2">
                    {new Date(comment.timestamp).toLocaleString()}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-stone-500 text-sm text-center py-8">No comments yet.</p>
          )}
        </>
      )}
    </div>
  );
}
export default ConflictHistory;
